import React from "react";
import { motion } from "framer-motion";
import {
  ScanSearch,
  ArrowRight,
  Sparkles,
  DatabaseZap,
} from "lucide-react";

interface FeatureCardType {
  id: string | number;
  icon: React.ReactNode;
  title: string;
  description: string;
}

interface FeatureCarouselProps {
  featureCards: FeatureCardType[];
}

const FeatureCarousel: React.FC<FeatureCarouselProps> = ({ featureCards }) => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut" as const,
      },
    },
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Flow Visualization */}
      <div className="relative mb-16">
        {/* Connecting Line - Hidden on mobile */}
        <div className="hidden md:block absolute top-24 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-[#00A3FF]/30 to-transparent" />

        <div className="grid grid-cols-1 md:grid-cols-5 gap-8 md:gap-4 items-start">
          {/* Start: Raw Documents */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6, delay: 0.1, ease: "easeOut" }}
            className="flex flex-col items-center text-center"
          >
            <div className="relative mb-6">
              <div className="w-20 h-20 rounded-2xl bg-white/5 border-2 border-white/20 flex items-center justify-center">
                <ScanSearch
                  className="w-10 h-10 text-white/60"
                  strokeWidth={1.5}
                />
              </div>
              <div className="absolute -top-2 -right-2 w-6 h-6 bg-amber-500/20 border border-amber-500/50 rounded-full flex items-center justify-center">
                <div className="w-2 h-2 bg-amber-500 rounded-full" />
              </div>
            </div>
            <h4 className="text-white font-semibold mb-2">Raw Documents</h4>
            <p className="text-gray-400 text-sm">
              PDFs, scans, handwritten notes
            </p>
          </motion.div>

          {/* Arrow 1 */}
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: 0.3, ease: "easeOut" }}
            className="hidden md:flex items-center justify-center pt-8"
          >
            <ArrowRight className="w-8 h-8 text-[#00A3FF]/50" />
          </motion.div>

          {/* Process */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
            className="flex flex-col items-center text-center"
          >
            <div className="relative mb-6 group">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#00A3FF] to-[#0077CC] flex items-center justify-center shadow-xl shadow-[#00A3FF]/30 transition-transform duration-300 group-hover:scale-110">
                <Sparkles className="w-10 h-10 text-white" strokeWidth={1.5} />
              </div>
              <div className="absolute inset-0 rounded-2xl bg-[#00A3FF]/20 blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </div>
            <h4 className="text-white font-semibold mb-2">AI Pipeline</h4>
            <p className="text-gray-400 text-sm">
              Extract, structure, recognise
            </p>
          </motion.div>

          {/* Arrow 2 */}
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: 0.5, ease: "easeOut" }}
            className="hidden md:flex items-center justify-center pt-8"
          >
            <ArrowRight className="w-8 h-8 text-[#00A3FF]/50" />
          </motion.div>

          {/* End: Structured Data */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6, delay: 0.3, ease: "easeOut" }}
            className="flex flex-col items-center text-center"
          >
            <div className="relative mb-6">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-emerald-500/20 to-emerald-600/20 border-2 border-emerald-500/50 flex items-center justify-center">
                <DatabaseZap
                  className="w-10 h-10 text-emerald-400"
                  strokeWidth={1.5}
                />
              </div>
              <div className="absolute -top-2 -right-2 w-6 h-6 bg-emerald-500/20 border border-emerald-500/50 rounded-full flex items-center justify-center">
                <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
              </div>
            </div>
            <h4 className="text-white font-semibold mb-2">Structured Data</h4>
            <p className="text-gray-400 text-sm">
              Searchable, actionable insights
            </p>
          </motion.div>
        </div>
      </div>

      {/* Feature Cards Grid */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-50px" }}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
      >
        {featureCards.map((card) => {
          const iconElement = React.isValidElement(card.icon)
            ? React.cloneElement(card.icon as React.ReactElement<any>, {
                className: "w-8 h-8 text-white",
                strokeWidth: 1.5,
              })
            : card.icon;

          return (
            <motion.div
              key={card.id}
              variants={itemVariants}
              whileHover={{ y: -8, transition: { duration: 0.2 } }}
              className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6
                         transition-all duration-300 hover:border-white/30 hover:bg-white/[0.07]
                         hover:shadow-xl hover:shadow-[#00A3FF]/10"
            >
              <div
                className="flex items-center justify-center w-14 h-14 rounded-full
                          bg-gradient-to-r from-[#00A3FF] to-[#0077CC]
                          shadow-lg shadow-[#00A3FF]/20 mb-4"
              >
                {iconElement}
              </div>
              <h3 className="text-white font-bold text-xl mb-3">{card.title}</h3>
              <p className="text-gray-300 text-sm leading-relaxed">
                {card.description}
              </p>
            </motion.div>
          );
        })}
      </motion.div>
    </div>
  );
};

export default FeatureCarousel;
